import { AddOutlined, CloseOutlined } from "@mui/icons-material";
import { Add } from "@mui/icons-material";
import CategoryIcon from "@mui/icons-material/Category";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import EmailIcon from "@mui/icons-material/Email";
import KeyIcon from "@mui/icons-material/Key";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import SourceIcon from "@mui/icons-material/Source";
import ViewCompactAltIcon from "@mui/icons-material/ViewCompactAlt";
import CircleNotificationsIcon from '@mui/icons-material/CircleNotifications';
import ScheduleIcon from "@mui/icons-material/Schedule";
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import Configuration from "./Configuration";
import IncidentPriorityMatrix from "./IncidentPriorityMatrix";
import Smtp from "./Smtp";
import SourceType from "./SourceType";
import SourceTypeDialog from "./SourceTypeDialog";
import SSO from "./SSO";
import Status from "./Status";
import StatusDialog from "./StatusDialog";
import TicketCategory from "./TicketCategory";
import TicketCategoryDialog from "./TicketCategoryDialog";
import Notifications from "./Notifications";
import NotificationDialog from "./NotificationDialog";
import TaskScheduler from "./TaskScheduler";
import TaskSchedulerDialog from "./TaskSchedulerDialog";
import License from "./License";
import LicenseDialog from "./LicenseDialog";
import Brands from "./Brands";
import BrandDialog from "./BrandDialog";
import Apis from "./Apis";
import ApiDialog from "./ApiDialog";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ApiIcon from '@mui/icons-material/Api';
import GenericCsvImportDialog from './GenericCsvImportDialog';
import GenericCsvImport from './GenericCsvImport';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssetStatus from "./AssetStatus";
import AssetStatusDialog from "./AssetStatusDialog";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import DisposalMethod from "./DisposalMethod";
import DisposalMethodDialog from "./DisposalMethodDialog";
import AssignmentType from "./AssignmentType";
import AssignmentTypeDialog from "./AssignmentTypeDialog";
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Warehouse from "./Warehouse";
import WarehouseDialog from "./WarehouseDialog";
import AssetLostType from "./AssetLostType";
import AssetLostTypeDialog from "./AssetLostTypeDialog";
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AssetMaintenanceType from "./AssetMaintenanceType";
import AssetMaintenanceTypeDialog from "./AssetMaintenanceTypeDialog";
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from "@mui/icons-material/Settings";
import PublicIcon from "@mui/icons-material/Public";
import GroupsIcon from "@mui/icons-material/Groups";
import ConfigurationCategory from "./ConfigurationCategory";

import ConfigurationEnvironment from "./ConfigurationEnvironment";

import ConfigurationMasterDialog from "./ConfigurationMasterDialog";

import ConfigurationRelationship from "./ConfigurationRelationship";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ChangeType from "./ChangeType";
import AffectedService from "./AffectedService";
import MaintenanceWindow from "./MaintenanceWindow";

import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";


// Dynamic Dialog Component
const DynamicDialog = ({
  open,
  onClose,
  title,
  children,
  description,
  AddComponent,
  AddComponentProps,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "50vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            borderBottom: 1,
            p: 2,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #23272f 0%, #3a4251 100%)"
                : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>

          <Stack
            direction={"row"}
            sx={{ display: "flex", gap: 1, alignItems: "center" }}
          >
            {AddComponent && <AddComponent {...AddComponentProps} />}
            <Tooltip title="Close">
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <CloseOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>{children}</DialogContent>
    </Dialog>
  );
};

const tiles = [
  {
    id: 1,
    title: "Incident Priority Matrix",
    icon: ViewCompactAltIcon,
    color: "success.main",
    component: IncidentPriorityMatrix,
    description: "Configure priority levels based on urgency and impact combinations",
  },
  {
    id: 2,
    title: "Source Type",
    icon: SourceIcon,
    color: "info.main",
    component: SourceType,
    description: "Manage source types",
    addComponent: SourceTypeDialog,
    addComponentProps: {
      srcTypeName: "Add Source Type",
      variant: "button",
    },
  },
  {
    id: 3,
    title: "Status",
    icon: DonutLargeIcon,
    color: "warning.main",
    component: Status,
    description: "Manage statuses",
    addComponent: StatusDialog,
    addComponentProps: {
      statusName: "Add Status",
      variant: "button",
    },
  },
  {
    id: 4,
    title: "Category",
    icon: CategoryIcon,
    color: "error.main",
    component: TicketCategory,
    description: "Manage ticket categories",
    addComponent: TicketCategoryDialog,
    addComponentProps: {
      categoryName: "Add Category",
      variant: "button",
    },
  },
  {
    id: 5,
    title: "SMTP",
    icon: EmailIcon,
    color: "secondary.main",
    component: Smtp,
    description: "Configure email server settings and authentication parameters",
  },
  {
    id: 6,
    title: "SSO",
    icon: KeyIcon,
    color: "error.dark",
    component: SSO,
    description: "Configure Single Sign-On settings and identity provider parameters",
  },
  {
    id: 7,
    title: "Global Configuration",
    icon: SettingsSuggestIcon,
    color: "success.dark",
    component: Configuration,
    description: "Manage global configuration",
  },
  {
    id: 8,
    title: "Notification",
    icon: CircleNotificationsIcon,
    color: "warning.main",
    component: Notifications,
    description: "Manage Notifications",
    addComponent: NotificationDialog,
    addComponentProps: {
      statusName: "Add Notification",
      variant: "button",
    },
  },
  {
    id: 9,
    title: "License",
    icon: FactCheckIcon,
    color: "secondary.main",
    component: License,
    description: "License",
    addComponent: LicenseDialog,
    addComponentProps: {
      statusName: "License",
      variant: "button",
    },
  },
  // {
  //   id: 10,
  //   title: "Task Scheduler",
  //   icon: ScheduleIcon,
  //   color: "primary.main",
  //   component: TaskScheduler,
  //   description: "Task Scheduler",
  //   addComponent: TaskSchedulerDialog,
  //   addComponentProps: {
  //     statusName: "Schedule Task",
  //     variant: "button",
  //   },
  // },
  {
    id: 10,
    title: "Branding",
    icon: BrandingWatermarkIcon,
    color: "error.main",
    component: Brands,
    description: "Branding",
    addComponent: BrandDialog,
    addComponentProps: {
      statusName: "Branding",
      variant: "button",
    },
  },
  {
    id: 11,
    title: "API",
    icon: ApiIcon,
    color: "success.main",
    component: Apis,
    description: "API",
    addComponent: ApiDialog,
    addComponentProps: {
      statusName: "API",
      variant: "button",
    },
  },
  {
    id: 12,
    title: "Data Import",
    icon: UploadFileIcon,
    color: "success.main",
    component: GenericCsvImport,
    description: "Data Import",
    addComponent: GenericCsvImportDialog,
    addComponentProps: {
      statusName: "DataImport",
      variant: "button",
    },
  },
  {
    id: 13,
    title: "Asset Status",
    icon: CurrencyExchangeIcon,
    color: "warning.main",
    component: AssetStatus,
    description: "Data Import",
    addComponent: AssetStatusDialog,
    addComponentProps: {
      statusName: "AssetStatus",
      variant: "button",
    },
  },
  {
    id: 14,
    title: "Asset Disposal Method",
    icon: RemoveCircleIcon,
    color: "error.main",
    component: DisposalMethod,
    description: "Asset Disposal Method",
    addComponent: DisposalMethodDialog,
    addComponentProps: {
      statusName: "DisposalMethod",
      variant: "button",
    },
  },
  {
    id: 14,
    title: "Asset Assignment Type",
    icon: AssignmentIcon,
    color: "secondary.main",
    component: AssignmentType,
    description: "Asset Assignment Type",
    addComponent: AssignmentTypeDialog,
    addComponentProps: {
      statusName: "AssignmentType",
      variant: "button",
    },
  },
  {
    id: 15,
    title: "Asset Warehouse",
    icon: WarehouseIcon,
    color: "warning.main",
    component: Warehouse,
    description: "Asset Warehouse",
    addComponent: WarehouseDialog,
    addComponentProps: {
      statusName: "Warehouse",
      variant: "button",
    },
  },
  {
    id: 16,
    title: "Asset Lost Type",
    icon: ReportProblemIcon,
    color: "error.main",
    component: AssetLostType,
    description: "Asset Lost Type",
    addComponent: AssetLostTypeDialog,
    addComponentProps: {
      statusName: "LostType",
      variant: "button",
    },
  },
  {
    id: 17,
    title: "Asset Maintenance Type",
    icon: BuildIcon,
    color: "warning.main",
    component: AssetMaintenanceType,
    description: "Asset Maintenance Type",
    addComponent: AssetMaintenanceTypeDialog,
    addComponentProps: {
      statusName: "MaintenanceType",
      variant: "button",
    },
  },
  {
    id: 18,
    title: "Configuration Category",
    icon: SettingsIcon,
    color: "primary.main",
    component: ConfigurationCategory,
    description: "Configuration Category",
    addComponent: ConfigurationMasterDialog,
    addComponentProps: {
      type: "category",
      variant: "button",
    },
  },
  {
    id: 19,
    title: "Configuration Environment",
    icon: PublicIcon,
    color: "success.main",
    component: ConfigurationEnvironment,
    description: "Configuration Environment",
    addComponent: ConfigurationMasterDialog,
    addComponentProps: {
      type: "environment",
      variant: "button",
    },
  },
  {
    id: 20,
    title: "Configuration Relationship",
    icon: AccountTreeIcon,
    color: "primary.main",
    component: ConfigurationRelationship,
    description: "Manage Configuration Item relationships",
    addComponent: ConfigurationMasterDialog,
    addComponentProps: {
      type: "relationship",
      variant: "button",
    },
  },
  {
    id: 21,
    title: "Change Maintenance Window",
    icon: BuildCircleIcon,
    color: "warning.main",
    component: MaintenanceWindow,
    description: "Manage Maintenace window",
    addComponent: ConfigurationMasterDialog,
    addComponentProps: {
      type: "maintenancewindow",
      variant: "button",
    },
  },
  {
    id: 22,
    title: "Affected Service",
    icon: MiscellaneousServicesIcon,
    color: "error.main",
    component: AffectedService,
    description: "Manage Affected Services",
    addComponent: ConfigurationMasterDialog,
    addComponentProps: {
      type: "affectedservice",
      variant: "button",
    },
  },
  {
    id: 23,
    title: "Change Type",
    icon: SwapHorizIcon,
    color: "primary.main",
    component: ChangeType,
    description: "Manage Change Types",
    addComponent: ConfigurationMasterDialog,
    addComponentProps: {
      type: "changetype",
      variant: "button",
    },
  },

];

function IconTileGrid() {
  const theme = useTheme();
  const [selectedTile, setSelectedTile] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTileProps, setSelectedTileProps] = useState(null);
  const handleTileClick = (tile) => {
    setSelectedTile(tile);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTile(null);
  };

  return (
    <>
      <Grid
        container
        spacing={3}
        justifyContent="center"
        alignItems="stretch"
        sx={{ mb: 4 }}
      >
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Grid item key={tile.id}>
              <Card
                sx={{
                  width: 200,
                  height: 180,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  borderRadius: 3,
                  boxShadow: theme.shadows[3],
                  background: theme.palette.background.paper,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-6px) scale(1.04)",
                    boxShadow: theme.shadows[8],
                  },
                  cursor: "pointer",
                }}
                tabIndex={0}
                aria-label={tile.title}
                onClick={() => handleTileClick(tile)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleTileClick(tile);
                  }
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Icon
                      sx={{
                        fontSize: 48,
                        color:
                          theme.palette[tile.color.split(".")[0]][
                          tile.color.split(".")[1]
                          ],
                      }}
                    />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {tile.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Dynamic Dialog */}
      {selectedTile && (
        <DynamicDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title={selectedTile.title}
          description={selectedTile.description}
          AddComponent={selectedTile.addComponent}
          AddComponentProps={selectedTile.addComponentProps}
        >
          <selectedTile.component />
        </DynamicDialog>
      )}
    </>
  );
}

export default IconTileGrid;
